import React, { useEffect, useContext } from 'react';
import { usePrevious } from 'hooks/usePrevious';
import classNames from "classnames";
import ChartBase from "./chart-base";
import { useChartBase, humanizeNumber, formatClassName } from './chart-base';
import SessionContext from 'SessionContext';
import TweenedNumber from "core/ui/tweened-number";
import { MapLocator } from "../mapping/map";
import LabelWithTooltip from './label-with-tooltip';

const LocationTypeWidget = (props) => {
  const base = useChartBase({ ...props, skipAutoFilterUpdate: true });
  const context = useContext(SessionContext);
  const prevFilters = usePrevious(props.filters);

  // Use custom renderChart if provided, otherwise use default
  const chartRenderer = props.renderChart
    ? () => props.renderChart({ ...props, base })
    : () => renderChart();

  useEffect(() => {
    const configID = props.config.id;
    const valueChange = checkForValueChange(prevFilters, props);
    const lengthChange = prevFilters && (prevFilters.length !== props.filters.length);
    if (valueChange || lengthChange) {
      props.onChartUpdate && props.onChartUpdate(true, false, configID);
    }
  // eslint-disable-next-line
  }, [props.filters]);

  // If the number of locations haven't changed, check for a value change on an ad-hoc radius
  function checkForValueChange(prevFilters, newProps) {
    if (!newProps?.filters?.length) { return false; }
    let changed = false;
    prevFilters?.forEach((filter, index) => {
      const prevValue = filter.value;
      const newValue = newProps.filters[index] && (newProps.filters[index].value || "");
      if (prevValue !== newValue) {
        changed = true;
      }
    });
    return changed;
  }

  function getClassName() {
    const filters = props.filters || [],
      isFiltered = filters.length,
      isNegated = isFiltered && filters[0].is_negated;
    return classNames("count-widget", { "is-negated": isNegated });
  }

  function renderChart() {
    if (Object.keys(props.values).length === 0 ) {
      return;
    }
    const config = props.config;
    const filters = props.filters || [];
    const viewOnly = props.viewOnly;
    const shouldTween = props.tween;
    const value_series = config.ds + "." + config.id;
    const values = props.values[value_series] || {};
    let chartIsNegated = 0;
    if (filters.length > 0) {
      filters.forEach((filter) => {
        if (filter.is_negated) {
          chartIsNegated = 1;
        }
      });
    }
    const displayFilters = chartIsNegated
      ? filters.map((filter) => ({ ...filter, is_negated: 1 }))
      : filters;
    let labels = config.value_labels;
    if (!labels || Object.keys(labels).length === 0) {
      labels = {};
      Object.entries(values).forEach(([val, _]) => (labels[val] = val));
    }

    let sortedList = base.sortListOutput(labels, values, displayFilters);
    let updatedLabelList = base.updatedLabelList;

    if(base.updatedLabelList && base.searchText.length) {
      sortedList = base.sortListOutput(updatedLabelList, values, displayFilters);
    }

    if(updatedLabelList && !Object.keys(updatedLabelList).length) {
      return <div className="flex-row">No Values Found</div>
    }

    if (!sortedList.length) {
      return;
    }

    // build the fly-to location
    let location_points = context.crossfilterStore.location_type_points;

    if(!location_points.length) {
      location_points = context.crossfilterStore.audience.location_type_points || [];
    }

    const locationPointsByName = {};
    location_points.forEach((val) => { locationPointsByName[val.name] = val; });

    return sortedList.map((item) => {
      const filterEntry = item.filtered,
        isFiltered = filterEntry && !viewOnly,
        isNegated = isFiltered && filterEntry.is_negated,
        value = item.value || 0,
        id = item.id,
        label = item.label;

      const points = locationPointsByName[id] || {lng: '', lat: ''};

      const output = (<div key={id} className="location-type-widget-row">
        {points.lng ? <MapLocator map={props.map} flyTo={{center: [points.lng, points.lat], zoom: 12}} /> : null}
        <div
          className={classNames("value", `${formatClassName(label)}-count`, {
            selected: isFiltered,
            not: isNegated || chartIsNegated,
          })}
          onClick={() => base.onFilterClick(
            !isFiltered, config, value_series, id, chartIsNegated
          )}
        >
          <LabelWithTooltip label={label} parentProps={props} cssClass="inner" />
          <i className="spacer"></i>
          {shouldTween && <TweenedNumber className="count" value={value} />}
          {!shouldTween && (
            <span className="count">{humanizeNumber(value)}</span>
          )}
        </div>
      </div>);
      return output;
    });
  }

  return (
    <ChartBase
      {...props}
      base={base}
      chartClassName={getClassName()}>
      <div className={getClassName()}>
        {chartRenderer()}
      </div>
    </ChartBase>
  );
}

export default LocationTypeWidget;
